/* ============================================================================
 * DisplayTablePreview
 * ----------------------------------------------------------------------------
 *  - identical header / error logic to DisplayCardPreview
 *  - simply renders DraftSubmissionTable (which now handles its own
 *    stub rows, column widths, resizers, etc.)
 * ========================================================================= */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';

import { useFormTheme }              from '../../../contexts/FormThemeContext';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';
import { useFormSubmissions }        from '../../../contexts/FormSubmissionsContext';   // 🆕 NEW import
import { useAuth }                   from '../../../contexts/AuthContext';              // ← add this
import { useFormContext }            from '../../../contexts/FormContext';              // ← add this
import { useNavigate }               from 'react-router-dom';                           // ← add this

import WarningIcon          from '@mui/icons-material/Warning';
import InsightsIcon         from '@mui/icons-material/Insights';                        // ← add this
import DraftSubmissionTable from './DraftSubmissionTable';
import SessionSubmissionTable from './SessionSubmissionTable';

//import styles               from './draftSubmissionTable.module.css';   // shared CSS

const DisplayTablePreview = ({
  /* -----------------------------------------------------------------------
   * NOTE:
   *  • submittedValues is still accepted so this component remains
   *    drop-in compatible with any future prop-based workflow.
   *  • It is NO LONGER the sole determinant of “hasValidSubmission”.
   * -------------------------------------------------------------------- */
  submittedValues = {},
  mode            = 'builder',   // "builder" | "final"
}) => {
  const { currentUser } = useAuth();                   // ← add this
  const { formId } = useFormContext();                 // ← add this
  const navigate = useNavigate();                      // ← add this
  const getAuthToken = React.useCallback(              // ← add this
    () => currentUser?.getIdToken?.() ?? null,
    [currentUser]
  );

  /* ---------- theme / outer layout ---------- */
  const { cardStyle }            = useFormTheme();

  const { submissionSettings }   = useFormSubmissionSettings();
  const {
    submissions = [],            // 🆕 rows fetched for all viewers
    userSubmissions = [],        // 🆕 viewer-only slice
  } = useFormSubmissions();

  /* -----------------------------------------------------------------------
   * Stabilize empty objects/arrays so props don’t change identity every render.
   * This helps React.memo() bails and reduces noisy logs in dev.
   * -------------------------------------------------------------------- */
  const EMPTY_OBJ = React.useMemo(() => ({}), []);
  const EMPTY_ARR = React.useMemo(() => [], []);

  /* Use a stable reference for addToSubmissionsTable settings */
  const actionSettingsRaw = submissionSettings.addToSubmissionsTable;
  const actionSettings = actionSettingsRaw || EMPTY_OBJ;

  const {
    cardTitle       = 'Submission Saved',
    cardDescription = 'Your entry was added to the table.',
    errorMessage,
    /* forwarded to DraftSubmissionTable (stabilized below) */
    columnOrder     : columnOrderRaw  = EMPTY_ARR,
    columnWidths    : columnWidthsRaw = EMPTY_OBJ,
  } = actionSettings;

  /* Ensure these props have stable identities when empty */
  const columnOrder  = columnOrderRaw  || EMPTY_ARR;
  const columnWidths = columnWidthsRaw || EMPTY_OBJ;

  /* -----------------------------------------------------------------------
   * Log-on-change: only log when columnOrder actually changes (by id list)
   * -------------------------------------------------------------------- */
  const lastOrderSigRef = React.useRef('');
  React.useEffect(() => {
    const orderIds = (columnOrder || []).map(c =>
      (typeof c === 'string' ? c : c?.id)
    );
    const sig = orderIds.join('|');
    if (sig !== lastOrderSigRef.current) {
      console.log(
        '[DisplayTablePreview] columnOrder from context:',
        orderIds
      );
      lastOrderSigRef.current = sig;
    }
  }, [columnOrder]);

  /* -----------------------------------------------------------------------
   *  hasValidSubmission:
   *    • builder mode   -> always TRUE  (table always shows while designing)
   *    • final mode     -> TRUE if either
   *        a. viewer-only slice has ≥ 1 row  (preferred), OR
   *        b. legacy prop submittedValues is non-empty (fallback)
   *
   *  NOTE:
   *    We intentionally DO NOT consider the global "submissions" slice here,
   *    so other users' rows never make this viewer's card appear "valid".
   * -------------------------------------------------------------------- */
  const hasViewerRows   = userSubmissions.length > 0;
  const legacyPropValid =
    submittedValues && Object.keys(submittedValues).length > 0;

  const hasValidSubmission =
    mode === 'builder' ? true : (hasViewerRows || legacyPropValid);

  const showHeader =
    mode === 'builder' || (mode === 'final' && hasValidSubmission);

  const showTable =
    mode === 'builder' || (mode === 'final' && hasValidSubmission);

  /* ---------- styled wrappers ---------- */
  const containerStyles = {
    backgroundColor: '#e0e0e0',
    padding:         '30px 0',
    width:           '100%',
    display:         'flex',
    justifyContent:  'center',
  };

  const cardStyles = {
    backgroundColor: cardStyle?.backgroundColor || '#ffffff',
    border:          `1px solid ${cardStyle?.borderColor || '#d9d9d9'}`,
    borderRadius:    0,
    padding:         cardStyle?.padding || '16px',
    fontSize:        cardStyle?.fontSize || '14px',
    lineHeight:      cardStyle?.lineHeight || 1.5,
    color:           cardStyle?.textColor || '#595959',
    maxWidth:        '95%',
    width:           '100%',
    margin:          '0 auto',
    boxSizing:       'border-box',
  };

  const tableContainerStyles = { mt: 0, width: '100%', overflow: 'auto' };

  /* ---------------------------- render --------------------------- */
  return (
    <Box sx={containerStyles}>
      <Box sx={{ ...cardStyles, minWidth: 0 }}>
        {/* ---------- card title / description ---------- */}
        {showHeader && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                align={cardStyle?.textAlign || 'left'}
                sx={{ color: cardStyle?.titleColor || 'inherit' }}
              >
                {cardTitle}
              </Typography>

              <Typography
                variant="body2"
                align={cardStyle?.textAlign || 'left'}
                sx={{ color: cardStyle?.descriptionColor || 'inherit', mb: 0 }}
              >
                {cardDescription}
              </Typography>
            </Box>

            {/* ────────────────────────────────────────────────────────────────
               Top-right drill-through to full submissions page
               - Only show in FINAL mode
               - Only show for logged-in users
               - Hidden for anonymous public users
               ──────────────────────────────────────────────────────────────── */}
            {mode === 'final' && !!currentUser && !!formId && (
              <Tooltip title="View all submissions">
                <IconButton
                  size="small"
                  aria-label="View all submissions"
                  onClick={() => navigate(`/forms/${formId}/submissions`)}
                  sx={{ mt: 0.25, flex: '0 0 auto' }}
                >
                  <InsightsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* ---------- error banner in final mode ---------- */}
        {mode === 'final' && !hasValidSubmission && (

          <Box
            sx={{
              mt: 0,
              display: 'flex',
              alignItems: 'center',
              color: 'red',
              fontSize: '14px',
            }}
          >
            <WarningIcon sx={{ mr: 1 }} />
            <Typography variant="body2" component="span">
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* ---------- table region ---------- */}
{showTable && (
  <Box sx={tableContainerStyles}>
    {mode === 'builder' ? (
      <DraftSubmissionTable
        columnOrder={columnOrder}
        columnWidths={columnWidths}
        stickyHeader={actionSettings.enableStickyHeader}
        getAuthToken={getAuthToken}
      />
    ) : (
      <SessionSubmissionTable />
    )}
  </Box>
)}

      </Box>
    </Box>
  );
};

export default DisplayTablePreview;
